import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AXIS, TOOLTIP_CURSOR, TOOLTIP_STYLE, toKey, toNumber } from './chart-theme';
import { useChartAnimation } from './use-chart-animation';

export interface GroupedBar {
  label: string;
  value: number;
  color: string;
  /** Text printed above the bar. Defaults to the value. */
  display?: string;
}

/**
 * A handful of labelled vertical bars with their values printed on top.
 *
 * For small comparisons (three billability buckets, four role levels) where an
 * axis would add chrome without adding information — the printed value is more
 * precise than reading a gridline, so the y-axis is hidden entirely.
 */
export function GroupedBarChart({
  bars,
  height = 130,
  onBarClick,
}: {
  bars: GroupedBar[];
  height?: number;
  onBarClick?: (index: number) => void;
}) {
  const animation = useChartAnimation();
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={bars} margin={{ top: 20, right: 8, bottom: 0, left: 8 }}>
          <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS.tick} />
          <YAxis hide domain={[0, 'dataMax']} />
          <Tooltip
            cursor={TOOLTIP_CURSOR}
            contentStyle={TOOLTIP_STYLE}
            formatter={(value, _key, item) => {
              const bar = item?.payload as GroupedBar | undefined;
              return [bar?.display ?? String(toNumber(value)), bar?.label ?? ''];
            }}
          />
          <Bar
            dataKey="value"
            radius={[5, 5, 0, 0]}
            maxBarSize={72}
            {...animation}
            cursor={onBarClick ? 'pointer' : undefined}
            onClick={(_, index) => onBarClick?.(index)}
          >
            {bars.map((b) => (
              <Cell key={b.label} fill={b.color} />
            ))}
            <LabelList dataKey="display" position="top" style={AXIS.label} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface StackedBar {
  label: string;
  /** Stack segments, drawn bottom-up. */
  segments: { key: string; value: number; color: string; label: string }[];
  /** Total printed above the stack. */
  display?: string;
}

/**
 * Vertical stacked bars — same role as {@link GroupedBarChart} but where each
 * category splits into parts (e.g. off-level hours as below-level + above-level).
 */
export function StackedBarChart({
  bars,
  height = 200,
  onBarClick,
}: {
  bars: StackedBar[];
  height?: number;
  onBarClick?: (index: number) => void;
}) {
  const animation = useChartAnimation();
  const keys = bars[0]?.segments.map((s) => ({ key: s.key, color: s.color, label: s.label })) ?? [];
  const data = bars.map((b) => {
    const row: Record<string, string | number> = { label: b.label, display: b.display ?? '' };
    for (const s of b.segments) row[s.key] = s.value;
    return row;
  });
  const labelFor = new Map(keys.map((k) => [k.key, k.label]));

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 20, right: 8, bottom: 4, left: 8 }}>
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: AXIS.line }}
            tick={AXIS.tick}
          />
          <YAxis hide />
          <Tooltip
            cursor={TOOLTIP_CURSOR}
            contentStyle={TOOLTIP_STYLE}
            formatter={(value, key) => [toNumber(value), labelFor.get(toKey(key)) ?? toKey(key)]}
          />
          {keys.map((k, i) => (
            <Bar
              key={k.key}
              dataKey={k.key}
              stackId="a"
              fill={k.color}
              radius={i === keys.length - 1 ? [4, 4, 0, 0] : 0}
              {...animation}
              cursor={onBarClick ? 'pointer' : undefined}
              onClick={(_, index) => onBarClick?.(index)}
            >
              {i === keys.length - 1 ? (
                <LabelList dataKey="display" position="top" style={AXIS.label} />
              ) : null}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
